import { supabase } from '../lib/supabase';

export const eventService = {
  // Get all published events with pagination
  async getEvents(options = {}) {
    const { 
      page = 1, 
      limit = 12, 
      category, 
      search, 
      featured = false,
      sortBy = 'event_date',
      sortOrder = 'asc'
    } = options;

    let query = supabase?.from('events')?.select(`
        *,
        event_categories(name, color),
        venues(name, city, state),
        user_profiles!organizer_id(full_name),
        ticket_types(id, name, price, quantity_available, quantity_sold)
      `)?.eq('status', 'published')?.gte('event_date', new Date()?.toISOString());

    // Apply filters
    if (category) {
      query = query?.eq('category_id', category);
    }

    if (search) {
      query = query?.or(`title.ilike.%${search}%, description.ilike.%${search}%`);
    }

    if (featured) {
      query = query?.eq('is_featured', true);
    }

    // Apply sorting
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query?.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching events:', error);
      throw error;
    }

    return {
      events: data || [],
      totalCount: count,
      hasMore: data?.length === limit
    };
  },

  // Get single event by ID
  async getEventById(eventId) {
    const { data, error } = await supabase?.from('events')?.select(`
        *,
        event_categories(id, name, color, icon),
        venues(*),
        user_profiles!organizer_id(full_name, email, phone),
        ticket_types(*)
      `)?.eq('id', eventId)?.single();

    if (error) {
      console.error('Error fetching event:', error);
      throw error;
    }

    return data;
  },

  // Get event categories
  async getCategories() {
    const { data, error } = await supabase?.from('event_categories')?.select('*')?.eq('is_active', true)?.order('name');

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data || [];
  },

  // Get featured events for homepage
  async getFeaturedEvents(limit = 6) {
    const { data, error } = await supabase?.from('events')?.select(`
        *,
        event_categories(name, color),
        venues(name, city, state),
        ticket_types(price)
      `)?.eq('status', 'published')?.eq('is_featured', true)?.gte('event_date', new Date()?.toISOString())?.order('event_date')?.limit(limit);

    if (error) {
      console.error('Error fetching featured events:', error);
      throw error;
    }

    return data || [];
  },

  // Search events
  async searchEvents(searchTerm) {
    const { data, error } = await supabase?.from('events')?.select(`
        *,
        event_categories(name, color),
        venues(name, city, state)
      `)?.eq('status', 'published')?.gte('event_date', new Date()?.toISOString())?.or(`title.ilike.%${searchTerm}%, description.ilike.%${searchTerm}%, short_description.ilike.%${searchTerm}%`)?.order('event_date')?.limit(20);

    if (error) {
      console.error('Error searching events:', error);
      throw error;
    }

    return data || [];
  },

  // Manager/Organizer functions
  async createEvent(eventData) {
    const { data, error } = await supabase?.from('events')?.insert(eventData)?.select()?.single();

    if (error) {
      console.error('Error creating event:', error);
      throw error;
    }

    return data;
  },

  async updateEvent(eventId, updates) {
    const { data, error } = await supabase?.from('events')?.update(updates)?.eq('id', eventId)?.select()?.single();

    if (error) {
      console.error('Error updating event:', error);
      throw error;
    }

    return data;
  },

  async deleteEvent(eventId) {
    const { error } = await supabase?.from('events')?.delete()?.eq('id', eventId);

    if (error) {
      console.error('Error deleting event:', error);
      throw error;
    }

    return true;
  },

  // Get events for a specific organizer
  async getOrganizerEvents(organizerId) {
    const { data, error } = await supabase?.from('events')?.select(`
        *,
        event_categories(name, color),
        venues(name, city, state),
        ticket_types(id, name, price, quantity_available, quantity_sold)
      `)?.eq('organizer_id', organizerId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching organizer events:', error);
      throw error;
    }

    return data || [];
  },

  // Get event statistics for dashboard
  async getEventStats(eventId) {
    const { data: event, error: eventError } = await supabase?.from('events')?.select(`
        *,
        ticket_types(id, name, quantity_available, quantity_sold, price),
        orders!event_id(id, status, total_amount, created_at),
        tickets!event_id(id, status, created_at)
      `)?.eq('id', eventId)?.single();

    if (eventError) {
      console.error('Error fetching event stats:', eventError);
      throw eventError;
    }

    // Calculate statistics
    const totalRevenue = event?.orders?.filter(o => o?.status === 'confirmed')?.reduce((sum, order) => sum + parseFloat(order?.total_amount), 0) || 0;

    const totalTicketsSold = event?.tickets?.filter(t => t?.status === 'sold')?.length || 0;
    const totalTicketsAvailable = event?.ticket_types?.reduce((sum, tt) => sum + tt?.quantity_available, 0) || 0;

    return {
      ...event,
      stats: {
        totalRevenue,
        totalTicketsSold,
        totalTicketsAvailable,
        salesRate: totalTicketsAvailable > 0 ? (totalTicketsSold / totalTicketsAvailable) * 100 : 0
      }
    };
  },

  // Upload event image
  async uploadEventImage(eventId, file, type = 'featured') {
    const fileName = `${eventId}/${type}_${Date.now()}.${file?.name?.split('.')?.pop()}`;
    
    const { data, error } = await supabase?.storage?.from('event-images')?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Error uploading event image:', error);
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase?.storage?.from('event-images')?.getPublicUrl(fileName);

    return urlData?.publicUrl;
  }
};
function getEventAnalytics(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getEventAnalytics is not implemented yet.', args);
  return null;
}

export { getEventAnalytics };
function getAllEvents(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getAllEvents is not implemented yet.', args);
  return null;
}

export { getAllEvents };
function createEvent(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createEvent is not implemented yet.', args);
  return null;
}

export { createEvent };
function updateEvent(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateEvent is not implemented yet.', args);
  return null;
}

export { updateEvent };
function duplicateEvent(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: duplicateEvent is not implemented yet.', args);
  return null;
}

export { duplicateEvent };